import { Box, Grid, Stack, Typography } from "@mui/material";
import { prefixClassName } from "@smartrr/shared/components/primitives";
import { AquariusCard } from "@smartrr/shared/customerPortal/components/AquariusCard";
import { IPurchaseState } from "@smartrr/shared/entities/PurchaseState";
import { ScheduledDeliveryGroupWithPurchSt } from "@smartrr/shared/entities/PurchaseState/scheduleUtils";
import { SellingPlanConfigs } from "@smartrr/shared/entities/SellingPlanGroup";
import { TrendingHeart } from "@smartrr/shared/icons/Aquarius/TrendingHeart";
import { typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import { sortProductsListByMethod } from "@smartrr/shared/utils/sortBy";
import React, { useEffect, useRef } from "react";

import { useSmartrrTheme } from "../../../hooks/useSmartrrTheme";
import { useMobileQuery } from "../../../utils";
import { usePurchasablesForTrending } from "../../hooks/usePurchasablesForTrending";
import { AddonList } from "../Subscription/AddonList";
import { getDeliveryId } from "@customer-app/state/stores/delivery/DeliveryId";

interface TrendingProps {
  delivery: ScheduledDeliveryGroupWithPurchSt<IPurchaseState>;
}

export const Trending = ({ delivery }: TrendingProps) => {
  const isMobile = useMobileQuery();
  const theme = useSmartrrTheme();
  const showSectionTitles = theme.modernThemeSettings?.showHide.showSectionTitles;

  const trendingProductList = usePurchasablesForTrending(delivery);

  const trendingSectionThemeData = theme.modernThemeSettings?.sections.trending;

  const boxRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!boxRef) {
      return;
    }
  }, [boxRef, trendingProductList]);

  if (!trendingProductList.length) {
    return <React.Fragment />;
  }

  return (
    <Box className={prefixClassName("trending-section-wrapper")} marginTop={"40px"}>
      <span className="anchor" id="trending" />
      {showSectionTitles ? (
        <Typography
          variant="h2"
          className={prefixClassName("trending-title")}
          data-testid="trending-section-title"
        >
          {typedLocalize("trending.title")}
        </Typography>
      ) : null}
      <AquariusCard className={prefixClassName("trending-card")} sx={{ padding: "20px 40px !important" }}>
        <Grid container className={prefixClassName("trending-wrapper")} display="flex" direction="row">
          <Grid item md={7} className={prefixClassName("trending-user")} display="flex" alignItems="center">
            <Grid
              container
              display="flex"
              direction="row"
              spacing={isMobile ? "15px" : "30px"}
              margin="0 auto"
              justifyContent={isMobile ? "center" : "start"}
              alignItems="center"
            >
              <Grid
                item
                data-testid="trending-image"
                className={prefixClassName("trending-image")}
                sx={{
                  display: "block !important",
                  background: `url(${trendingSectionThemeData?.imgUrl})`,
                  height: "150px",
                  width: "150px",
                  backgroundPosition: "center center",
                  backgroundSize: "cover",
                  backgroundRepeat: "no-repeat",
                  borderRadius: "137px",
                  position: "relative",
                  "& svg": {
                    position: "absolute",
                    bottom: "0px",
                    right: "0px",
                  },
                }}
              >
                {trendingSectionThemeData?.showHeart ? <TrendingHeart data-testid="trending-heart" /> : null}
              </Grid>

              <Grid
                item
                container
                md={8}
                direction="column"
                justifyContent="center"
                textAlign={isMobile ? "center" : "start"}
                paddingTop="0px !important"
              >
                <Typography
                  variant={isMobile ? "h4" : "h3"}
                  mt={isMobile ? "15px" : "0"}
                  className={prefixClassName("trending-section-header")}
                  data-testid="trending-heading"
                >
                  {trendingSectionThemeData?.heading}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ opacity: "80%" }}
                  dangerouslySetInnerHTML={{ __html: trendingSectionThemeData?.caption ?? "" }}
                  className={prefixClassName("trending-section-subheader")}
                  data-testid="trending-body"
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid
            item
            md={5}
            className={prefixClassName("trending-products")}
            sx={{ width: "100%", marginTop: "15px" }}
          >
            <Stack
              direction="row"
              display="flex"
              justifyContent={isMobile && trendingProductList.length >= 3 ? "start" : "center"}
              overflow="scroll"
              margin={isMobile ? "0 -40px 0 -20px" : "0px"}
              className={prefixClassName("trending-list-container")}
            >
              <AddonList
                deliveryId={getDeliveryId(delivery)}
                purchasables={sortProductsListByMethod(trendingProductList, trendingSectionThemeData?.sortMethod)}
                configType={SellingPlanConfigs.TRENDING}
                portalNodeEleOverride={boxRef}
              />
            </Stack>
            <Box ref={boxRef}></Box>
          </Grid>
        </Grid>
      </AquariusCard>
    </Box>
  );
};
